import { useEffect } from "react";
import { addNotification } from "../../redux/slices/notification-slice";
import { store } from "../../redux/store";

export const RECAPTCHA_SCRIPT_ID = "google-recaptcha-enterprise";

/** Backend, assessment sırasında bu action ile eşleşme arar. */
export const RECAPTCHA_ACTION = "homepage";

export const RECAPTCHA_ERROR_MESSAGE =
	"Güvenlik doğrulaması (reCAPTCHA) tamamlanamadı. Lütfen sayfayı yenileyip tekrar deneyiniz, sorun devam ederse bizimle iletişime geçiniz.";

interface GoogleReCaptchaProps {
	siteKey?: string;
	setGoogleReCaptchaToken: (token: string) => void;
}

const getBadgeContainer = (): HTMLElement | null => {
	const badge = document.querySelector(".grecaptcha-badge");
	return (badge?.parentElement as HTMLElement) ?? null;
};

/**
 * enterprise.js sayfa başına yalnızca bir kez yüklenmelidir. Aynı script birden
 * fazla kez eklendiğinde grecaptcha yeniden başlatılır ve execute çağrıları
 * "Invalid site key or not loaded in api.js" hatası ile reddedilir.
 *
 * render= parametresi yalnızca score-based (skor tabanlı) anahtarlar için
 * geçerlidir; checkbox tipi bir anahtar ile Google 400 döner ve script hiç
 * yüklenmez.
 */
const loadRecaptchaScript = (siteKey: string): Promise<void> =>
	new Promise((resolve, reject) => {
		const existingScript = document.getElementById(
			RECAPTCHA_SCRIPT_ID
		) as HTMLScriptElement | null;

		if (existingScript !== null) {
			if (existingScript.dataset.loaded === "true") {
				resolve();
			} else {
				existingScript.addEventListener("load", () => resolve());
				existingScript.addEventListener("error", () =>
					reject(new Error("reCAPTCHA script could not be loaded."))
				);
			}
			return;
		}

		const script = document.createElement("script");
		script.id = RECAPTCHA_SCRIPT_ID;
		script.src =
			"https://www.google.com/recaptcha/enterprise.js?render=" + siteKey;
		script.async = true;
		script.addEventListener("load", () => {
			script.dataset.loaded = "true";
			resolve();
		});
		script.addEventListener("error", () =>
			reject(new Error("reCAPTCHA script could not be loaded."))
		);
		document.body.appendChild(script);
	});

const executeRecaptcha = (siteKey: string): Promise<string> =>
	new Promise((resolve, reject) => {
		const enterprise = window.grecaptcha?.enterprise;
		if (enterprise === undefined) {
			reject(new Error("grecaptcha.enterprise is not available on window."));
			return;
		}

		enterprise.ready(() => {
			try {
				Promise.resolve(
					enterprise.execute(siteKey, { action: RECAPTCHA_ACTION })
				).then(resolve, reject);
			} catch (error) {
				reject(error);
			}
		});
	});

const GoogleReCaptcha = (props: GoogleReCaptchaProps) => {
	const { siteKey, setGoogleReCaptchaToken } = props;

	useEffect(() => {
		const submitButton = document.getElementById("submit");
		if (submitButton === null) {
			return;
		}

		let isMounted = true;
		const hasSiteKey = siteKey !== undefined && siteKey !== "";

		const reportFailure = (message: string, error?: unknown) => {
			console.error(message, error);
			store.dispatch(
				addNotification({
					message: RECAPTCHA_ERROR_MESSAGE,
					type: "danger",
				})
			);
		};

		// Butona basılmadan önce yükleyip tıklama anındaki gecikmeyi önlüyoruz.
		// Yükleme hatası burada değil, tıklama akışında kullanıcıya bildiriliyor.
		const scriptPromise = hasSiteKey
			? loadRecaptchaScript(siteKey as string)
			: null;
		scriptPromise?.catch(() => undefined);

		const handleClick = () => {
			// Site key boşsa /recaptcha ya hiç dönmemiş ya da yapılandırma eksik.
			// Sessizce çıkmak butonu ölü bırakır; kullanıcıya durumu bildiriyoruz.
			if (scriptPromise === null) {
				reportFailure(
					"reCAPTCHA site key is missing. Check GET /recaptcha and the GOOGLE_RECAPTCHA_SITE_KEY configuration."
				);
				return;
			}

			scriptPromise
				.then(() => executeRecaptcha(siteKey as string))
				.then((token: string) => {
					if (isMounted) {
						setGoogleReCaptchaToken(token);
					}
				})
				.catch((error: unknown) => {
					reportFailure("reCAPTCHA execute failed.", error);
				});
		};

		submitButton.addEventListener("click", handleClick);

		return () => {
			isMounted = false;
			submitButton.removeEventListener("click", handleClick);
		};
	}, [siteKey, setGoogleReCaptchaToken]);

	useEffect(() => {
		const badgeContainer = getBadgeContainer();
		if (badgeContainer !== null) {
			badgeContainer.style.display = "";
		}

		// Rozet DOM'dan silinirse tekrar mount olduğunda grecaptcha bozuluyor,
		// bu yüzden kaldırmak yerine yalnızca gizliyoruz.
		return () => {
			const containerOnUnmount = getBadgeContainer();
			if (containerOnUnmount !== null) {
				containerOnUnmount.style.display = "none";
			}
		};
	}, []);

	return null;
};

export default GoogleReCaptcha;
